// src/components/consulta.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Spinner,
  Alert,
} from "react-bootstrap";
import { cnpj, cpf } from "cpf-cnpj-validator";
import ToastNotification from "./ToastNotification";
import PartnerCardPreview from "./PartnerCardPreview";
import { API_URL } from "../apiConfig";
import { getAccessToken, logout } from "../auth";

function isDocValid(d) {
  const n = (d || "").replace(/\D/g, "");
  if (!n) return false;
  return n.length <= 11 ? cpf.isValid(n) : cnpj.isValid(n);
}

export default function Consulta() {
  const navigate = useNavigate();
  const [doc, setDoc] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { isClient, firstName, maskedDocument }

  const [toast, setToast] = useState({
    show: false,
    title: "",
    message: "",
    variant: "success",
  });

  const showToast = (title, message, variant = "success") => {
    setToast({ show: true, title, message, variant });
  };

  const token = getAccessToken();
  const [partner, setPartner] = useState(null);

  // fetch partner info for preview (only for logged partner users)
  useEffect(() => {
    async function loadPartner() {
      if (!token) return;
      if (!API_URL) return;
      try {
        const r = await fetch(`${API_URL}/partners/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const data = await r.json();
        if (data && data.partner) setPartner(data.partner);
      } catch (e) {
        // ignore silently
      }
    }
    loadPartner();
  }, [token]);

  useEffect(() => {
    if (!token) {
      showToast(
        "Acesso negado",
        "Você precisa fazer login para acessar esta página.",
        "danger"
      );
      setTimeout(() => navigate("/"), 2000);
    }
  }, [token, navigate]);

  async function handleConsulta() {
    const raw = (doc || "").replace(/\D/g, "");
    if (!isDocValid(doc)) {
      return showToast("Erro", "CPF/CNPJ inválido.", "danger");
    }
    if (!API_URL) {
      return showToast(
        "Erro",
        "API_URL não definida. Confira seu .env",
        "danger"
      );
    }
    if (!token) {
      showToast(
        "Acesso negado",
        "Token não encontrado. Faça login novamente.",
        "danger"
      );
      setTimeout(() => navigate("/"), 2000);
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      const r = await fetch(`${API_URL}/checks`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ document: raw }),
      });
      const data = await r.json();

      // Se retornar 401 ou 403, redirecionar para login
      if (r.status === 401 || r.status === 403) {
        await logout({ remote: false });
        showToast("Sessão expirada", "Faça login novamente.", "danger");
        setTimeout(() => navigate("/"), 2000);
        return;
      }

      if (!r.ok) {
        return showToast(
          "Erro na consulta",
          data?.message || "Erro ao consultar",
          "danger"
        );
      }

      setResult({
        isClient: !!data.isClient,
        firstName: data.firstName ?? null,
        maskedDocument: data.maskedDocument,
      });

      showToast(
        data.isClient ? "Pessoa verificada" : "Não encontrado",
        data.isClient
          ? `${data.firstName || "Pessoa"} é membro do Euro Privilege Club!`
          : "Esta pessoa não foi encontrada em nossos registros.",
        data.isClient ? "success" : "warning"
      );
    } catch (e) {
      showToast("Erro de conexão", "Falha ao conectar na API", "danger");
    } finally {
      setLoading(false);
    }
  }

  async function handleLogout() {
    await logout();
    showToast("Logout", "Você saiu com sucesso!", "success");
    setTimeout(() => navigate("/"), 1000);
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f7" }}>
      <Container className="py-4 h-100 d-flex align-items-center">
        <Row className="w-100 justify-content-center align-items-center g-2">
          <Col xs={12} lg={7}>
            {/* Welcome banner for the logged partner (above the main card) */}
            {partner && (
              <div className="text-center mb-3">
                <h5 className="fw-semibold mb-0">Bem-vindo, {partner.company || 'Parceiro'}</h5>
                <div className="text-muted" style={{ fontSize: 14 }}>Aqui está seu preview e ferramentas rápidas</div>
              </div>
            )}

            <Card className="shadow-sm border" style={{ borderRadius: 16 }}>
              <Card.Body className="p-4">
                <Row className="align-items-center mb-2">
                  <Col>
                    <h4 className="fw-bold mb-0">Consultar Membro</h4>
                  </Col>
                  <Col className="text-end">
                    <Button
                      variant="link"
                      className="text-dark fw-semibold"
                      onClick={handleLogout}
                    >
                      Sair
                    </Button>
                  </Col>
                </Row>

                {/* Texto orientativo atualizado */}
                <div className="text-muted mb-2">
                  Digite o <strong>CPF ou CNPJ</strong> exatamente como{" "}
                  <strong>cadastrado na Euro Condomínios</strong> para verificar
                  se a pessoa é membro do <strong>Euro Privilege Club</strong>.
                </div>

                <Form.Group className="mb-3">
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9.\-/\s]*"
                    placeholder="Digite o CPF/CNPJ (somente números)"
                    value={doc}
                    onChange={(e) => setDoc(e.target.value)}
                  />
                </Form.Group>

                <div className="d-grid mb-2">
                  <Button
                    variant="dark"
                    size="lg"
                    onClick={handleConsulta}
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="d-inline-flex align-items-center gap-2">
                        <Spinner animation="border" size="sm" /> Consultando
                      </span>
                    ) : (
                      "Consultar"
                    )}
                  </Button>
                </div>

                {result ? (
                  <Card className="mt-3 border-1" style={{ borderRadius: 14 }}>
                    <Card.Body className="d-flex align-items-center gap-3">
                      <div
                        className="d-inline-flex align-items-center justify-content-center"
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 999,
                          background: result.isClient ? "#E8F6EE" : "#FDECEE",
                        }}
                      >
                        <span
                          className={`fs-5 ${
                            result.isClient ? "text-success" : "text-danger"
                          }`}
                          style={{ lineHeight: 1 }}
                        >
                          {result.isClient ? "✓" : "✕"}
                        </span>
                      </div>

                      <div className="flex-grow-1">
                        <div className="fw-semibold">Euro Privilege Club</div>
                        <div
                          className={`fw-semibold ${
                            result.isClient ? "text-success" : "text-danger"
                          }`}
                        >
                          {result.isClient
                            ? "Pessoa verificada"
                            : "Não encontrado"}
                        </div>

                        {/* Exibe apenas o primeiro nome, sem condomínio/cidade/UF */}
                        {result.firstName ? (
                          <div className="fw-semibold fs-5 text-dark">
                            {result.firstName}
                          </div>
                        ) : null}
                      </div>
                    </Card.Body>
                  </Card>
                ) : !loading ? (
                  <Alert variant="light" className="mt-3 border">
                    Nenhum resultado ainda. Faça uma consulta.
                  </Alert>
                ) : null}
              </Card.Body>
            </Card>
          </Col>
          {/* Partner preview column on large screens; stacked on small screens */}
          <Col xs={12} lg={4} className="d-none d-lg-flex align-items-start justify-content-center">
            {partner && (
              <div style={{ width: 300 }}>
                <div style={{ textAlign: 'center', marginBottom: 10 }}>
                  <span style={{ display: 'inline-block', background: '#e6f9f0', color: '#047857', padding: '0.25rem 0.5rem', borderRadius: 8, fontSize: '0.85rem', fontWeight: 700 }}>
                    Parceiro em destaque
                  </span>
                  <div style={{ fontSize: '0.85rem', color: '#0f172a', marginTop: 6 }}>
                    Parabéns — sua oferta está ao vivo em{' '}
                    <a href="https://eurocondominios.com.br/privilege/" target="_blank" rel="noreferrer" style={{ color: '#065f46', fontWeight: 700, textDecoration: 'none' }}>
                      eurocondominios.com.br/privilege/
                    </a>
                  </div>
                </div>
                <PartnerCardPreview item={partner} />
              </div>
            )}
          </Col>
        </Row>
      </Container>

      {/* Toast Notification */}
      <ToastNotification
        show={toast.show}
        onClose={() => setToast({ ...toast, show: false })}
        title={toast.title}
        message={toast.message}
        variant={toast.variant}
      />
    </div>
  );
}
